import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Lightweight modal to send an assessment/interview to a candidate by name + email.
 * Props:
 *   open, onClose
 *   onSend({ candidate_name, candidate_email }) -> Promise
 *   title       header text
 *   accent      accent color (button/border)
 */
export const SendToCandidateDialog = ({ open, onClose, onSend, title = 'Send to candidate', accent = '#0076A3' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const close = () => { if (!sending) { setName(''); setEmail(''); onClose(); } };

  const submit = async () => {
    if (!email.trim()) { toast.error('Candidate email is required'); return; }
    setSending(true);
    try {
      await onSend({ candidate_name: name.trim(), candidate_email: email.trim() });
      setName(''); setEmail('');
      onClose();
    } catch (e) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="send-to-candidate-dialog"
      onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: '#002A38' }}>{title}</h3>
          <button data-testid="send-dialog-close" onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94A3B8' }}><X size={20} /></button>
        </div>

        <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>Candidate name</label>
        <input
          data-testid="send-dialog-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          style={{ width: '100%', marginTop: '0.375rem', marginBottom: '0.875rem', padding: '0.625rem 0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', fontSize: '0.875rem', outline: 'none' }}
        />

        <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>Candidate email *</label>
        <input
          data-testid="send-dialog-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@example.com"
          style={{ width: '100%', marginTop: '0.375rem', marginBottom: '1.25rem', padding: '0.625rem 0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', fontSize: '0.875rem', outline: 'none' }}
        />

        <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'flex-end' }}>
          <button onClick={close} disabled={sending} style={{ padding: '0.5rem 1rem', borderRadius: 999, background: '#F1F5F9', color: '#475569', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button
            data-testid="send-dialog-submit"
            onClick={submit}
            disabled={sending}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.1rem', borderRadius: 999, background: accent, color: '#fff', border: 'none', fontWeight: 600, cursor: sending ? 'wait' : 'pointer', opacity: sending ? 0.7 : 1 }}
          >
            <Send size={15} /> {sending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendToCandidateDialog;
